import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../../components/Button';
import { ChoiceGroup } from '../../components/Choice';
import { Field, Input, Select, Textarea } from '../../components/Field';
import { Card, Page } from '../../components/Page';
import { useDb } from '../../db/hooks';
import { ACCESS_LABELS, MODALITY_LABELS, saveProfile, useProfile, type AccessType, type Profile } from '../../data/profile';
import { useCanWrite } from '../../account/useCanWrite';
import { inputValue, optionalNumber, optionalPastDate, optionalText } from '../../lib/forms';

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const schema = z.object({
  full_name: optionalText(120),
  dob: optionalPastDate,
  sex: optionalText(20),
  blood_group: optionalText(5),
  diagnosis: optionalText(300),
  comorbidities: optionalText(500),
  allergies: optionalText(300),
  dialysis_start_date: optionalPastDate,
  dry_weight_kg: optionalNumber(20, 300, 'Enter a weight between 20 and 300 kg.'),
  access_type: z.enum(['avf', 'avg', 'catheter']).nullable(),
  access_location: optionalText(120),
  access_created_on: optionalPastDate,
  nephrologist_name: optionalText(120),
  center_name: optionalText(120),
  center_phone: optionalText(40),
  emergency_contact_name: optionalText(120),
  emergency_contact_phone: optionalText(40)
});

type FormInput = z.input<typeof schema>;
type FormOutput = z.output<typeof schema>;

function toForm(profile: Profile | null): FormInput {
  return {
    full_name: inputValue(profile?.full_name),
    dob: inputValue(profile?.dob),
    sex: inputValue(profile?.sex),
    blood_group: inputValue(profile?.blood_group),
    diagnosis: inputValue(profile?.diagnosis),
    comorbidities: inputValue(profile?.comorbidities),
    allergies: inputValue(profile?.allergies),
    dialysis_start_date: inputValue(profile?.dialysis_start_date),
    dry_weight_kg: inputValue(profile?.dry_weight_kg),
    access_type: profile?.access_type ?? null,
    access_location: inputValue(profile?.access_location),
    access_created_on: inputValue(profile?.access_created_on),
    nephrologist_name: inputValue(profile?.nephrologist_name),
    center_name: inputValue(profile?.center_name),
    center_phone: inputValue(profile?.center_phone),
    emergency_contact_name: inputValue(profile?.emergency_contact_name),
    emergency_contact_phone: inputValue(profile?.emergency_contact_phone)
  };
}

export function ProfilePage() {
  const db = useDb();
  const canWrite = useCanWrite();
  const { profile, isLoading } = useProfile();
  const [saved, setSaved] = useState(false);
  const form = useForm<FormInput, unknown, FormOutput>({ resolver: zodResolver(schema), defaultValues: toForm(null) });
  const { errors, isSubmitting, isDirty } = form.formState;

  // Load the stored profile once it arrives (and again if sync brings a newer one while untouched).
  useEffect(() => {
    if (!isLoading && !form.formState.isDirty) form.reset(toForm(profile));
  }, [profile, isLoading, form]);

  async function submit(values: FormOutput) {
    try {
      await saveProfile(db, values);
      form.reset(form.getValues());
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (error) {
      form.setError('root', { message: error instanceof Error ? error.message : String(error) });
    }
  }

  return (
    <Page title="Profile" back="/more">
      <form noValidate onSubmit={form.handleSubmit(submit)}>
        <fieldset disabled={!canWrite} className="space-y-4">
          <Card title="About you">
            <div className="space-y-4">
              <Field label="Full name" error={errors.full_name?.message}>
                <Input autoComplete="name" {...form.register('full_name')} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Date of birth" error={errors.dob?.message}>
                  <Input type="date" {...form.register('dob')} />
                </Field>
                <Field label="Sex">
                  <Select {...form.register('sex')}>
                    <option value="">Not set</option>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                    <option value="other">Other</option>
                  </Select>
                </Field>
              </div>
              <Field label="Blood group">
                <Select {...form.register('blood_group')}>
                  <option value="">Not set</option>
                  {BLOOD_GROUPS.map((group) => (
                    <option key={group} value={group}>
                      {group}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Allergies" error={errors.allergies?.message}>
                <Input {...form.register('allergies')} placeholder="e.g. Penicillin" />
              </Field>
            </div>
          </Card>

          <Card title="Kidney care">
            <div className="space-y-4">
              <p className="text-sm">
                <span className="text-clinical-muted">Dialysis type: </span>
                <span className="font-medium">{profile?.modality ? MODALITY_LABELS[profile.modality] : 'Not set'}</span>{' '}
                <Link to="/sessions" className="text-clinical-primary underline">
                  Change in Sessions
                </Link>
              </p>
              <Field label="Cause of kidney failure" error={errors.diagnosis?.message}>
                <Input {...form.register('diagnosis')} placeholder="e.g. Diabetic nephropathy" />
              </Field>
              <Field label="Other conditions" error={errors.comorbidities?.message}>
                <Textarea {...form.register('comorbidities')} placeholder="e.g. Diabetes, high blood pressure" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Started dialysis" error={errors.dialysis_start_date?.message}>
                  <Input type="date" {...form.register('dialysis_start_date')} />
                </Field>
                <Field label="Dry weight (kg)" error={errors.dry_weight_kg?.message}>
                  <Input inputMode="decimal" {...form.register('dry_weight_kg')} />
                </Field>
              </div>
            </div>
          </Card>

          <Card title="Access">
            <div className="space-y-4">
              <Controller
                control={form.control}
                name="access_type"
                render={({ field }) => (
                  <ChoiceGroup<AccessType>
                    name="access_type"
                    value={field.value ?? null}
                    onChange={field.onChange}
                    columns={3}
                    options={(Object.keys(ACCESS_LABELS) as AccessType[]).map((value) => ({ value, label: ACCESS_LABELS[value] }))}
                  />
                )}
              />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Location" error={errors.access_location?.message}>
                  <Input {...form.register('access_location')} placeholder="e.g. Left forearm" />
                </Field>
                <Field label="Created on" error={errors.access_created_on?.message}>
                  <Input type="date" {...form.register('access_created_on')} />
                </Field>
              </div>
            </div>
          </Card>

          <Card title="Care team">
            <div className="space-y-4">
              <Field label="Nephrologist" error={errors.nephrologist_name?.message}>
                <Input {...form.register('nephrologist_name')} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Dialysis unit" error={errors.center_name?.message}>
                  <Input {...form.register('center_name')} />
                </Field>
                <Field label="Unit phone" error={errors.center_phone?.message}>
                  <Input type="tel" autoComplete="off" {...form.register('center_phone')} />
                </Field>
              </div>
            </div>
          </Card>

          <Card title="Emergency contact">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Contact name" error={errors.emergency_contact_name?.message}>
                <Input {...form.register('emergency_contact_name')} />
              </Field>
              <Field label="Contact phone" error={errors.emergency_contact_phone?.message}>
                <Input type="tel" autoComplete="off" {...form.register('emergency_contact_phone')} />
              </Field>
            </div>
          </Card>

          {errors.root ? <p className="text-sm text-clinical-danger">{errors.root.message}</p> : null}
          {canWrite ? (
            <Button type="submit" className="w-full" disabled={isSubmitting || !isDirty}>
              {isSubmitting ? 'Saving…' : saved ? 'Saved' : 'Save profile'}
            </Button>
          ) : (
            <p className="text-center text-sm text-clinical-muted">Your plan has lapsed, so the profile is read-only.</p>
          )}
        </fieldset>
      </form>
    </Page>
  );
}
